"use client";

import React from "react";
import { ArrowRight, Phone, ShieldCheck, CircleCheck as CheckCircle2, Building2, House as Home, Hammer, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const services = [{
  label: "Commercial",
  icon: Building2
}, {
  label: "Residential",
  icon: Home
}, {
  label: "Roofing",
  icon: Hammer
}, {
  label: "Specialist access",
  icon: Compass
}];
const accreditations = ["NASC verified", "CHAS accredited"];
function HeroBracknellImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => p.customFields?.photo_name === "Bracknell Scaffolding Hero Image" || p.title === "Bracknell Scaffolding Hero Image");
  const imgSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const imgAlt = matchedPhoto?.customFields?.alt_text || "BMF Scaffolding structure installation in Bracknell";
  if (imgSrc) {
    return <img src={imgSrc} data-wvc-srcset="" data-wvc-sizes="" alt={imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_bracknell_scaffolding.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_bracknell_scaffolding.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hero_bracknell_scaffolding.png-wvc-sizes" alt="BMF Scaffolding structure installation in Bracknell" loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
export default function HeroBracknell() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 50
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="herobracknell" data-nav="dark" className="relative min-h-[85vh] flex items-center bg-secondary text-secondary-foreground overflow-hidden py-16 md:py-24">
      {/* Background Architectural Grid & Subtle Radial Glow */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute -top-40 right-0 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Hero Copy Column */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            {/* Top Accreditation Pill */}
            <div className="flex items-center gap-2 mb-6">
              <Badge variant="secondary" className="bg-sidebar-accent text-secondary-foreground border-sidebar-border px-3 py-1 text-xs sm:text-sm font-medium rounded-full flex items-center gap-1.5 shadow-xs">
                <ShieldCheck className="size-4 text-primary" />
                <span>NASC & CHAS accredited</span>
              </Badge>
            </div>

            {/* H1 Headline */}
            <h1 className="font-default text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight md:leading-[1.1] mb-6">
              Scaffolding in Bracknell
            </h1>

            {/* Proposition */}
            <p className="text-base sm:text-lg md:text-xl text-secondary-foreground/85 leading-relaxed max-w-2xl mb-8">
              Professional, safety-led scaffolding for commercial sites, residential builds, roofing contracts, and complex specialist access across Bracknell and Berkshire.
            </p>

            {/* Call to Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-10">
              <Button asChild size="lg" className="h-12 px-7 text-base font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base font-semibold border-sidebar-border bg-sidebar/60 text-secondary-foreground hover:bg-sidebar hover:text-secondary-foreground">
                <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                  <Phone className="size-4 text-primary" />
                  <span className="text-secondary-foreground">Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Core Sectors Tag Grid */}
            <div className="pt-6 border-t border-sidebar-border/70">
              <p className="text-xs uppercase tracking-wider font-semibold text-secondary-foreground/70 mb-3">
                Core scaffolding disciplines
              </p>
              <div className="flex flex-wrap gap-2.5">
                {services.map((service, i) => {
                const Icon = service.icon;
                return <div key={service.label} data-index={i} className="inline-flex items-center gap-2 rounded-md bg-sidebar-accent/80 border border-sidebar-border px-3 py-1.5 text-xs sm:text-sm font-medium text-secondary-foreground">
                      <Icon className="size-3.5 text-primary" />
                      <span>{service.label}</span>
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Right Visual & Trust Panel */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-2xl overflow-hidden border border-sidebar-border bg-sidebar/80 shadow-2xl backdrop-blur-xs p-2">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-sidebar">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 50,
                status: ["publish"]
              }}>
                  <HeroBracknellImage />
                </PostsProvider>
                <div className="absolute inset-0 bg-gradient-to-t from-sidebar via-transparent to-transparent opacity-80 pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                  <span className="inline-block bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-sm uppercase tracking-wider mb-1">
                    Bracknell & Surrounding Areas
                  </span>
                  <p className="text-sm font-medium text-secondary-foreground">
                    Fully compliant structures engineered for safety and trade access.
                  </p>
                </div>
              </div>

              {/* Accreditations Trust Strip */}
              <div className="p-4 bg-sidebar-accent/40 rounded-lg mt-2 border border-sidebar-border/50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-2 text-xs font-medium text-secondary-foreground/90">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}